import {
  MutationFunction,
  QueryClient,
  QueryClientProvider,
  QueryFunctionContext,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { FC, PropsWithChildren, createContext, useMemo } from 'react';
import { ZodSchema } from 'zod';
import { useRequestToken } from '../authentication/hooks/auth';
import { useVariable } from '@softwareimaging/backstage';
import { Variables } from '../backstage/config';

const context = createContext<QueryClient | undefined>(undefined);

type RequestOptions = Omit<RequestInit, 'body'> & { body?: any };
export type QueryParams = [string] | [string, RequestOptions];

export const APIProvider: FC<PropsWithChildren> = ({ children }) => {
  const client = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          suspense: true,
          retry: 1,
          staleTime: 1000 * 60 * 5
        }
      }
    });
  }, []);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
};

export function useAPIRequest() {
  const apiBaseUrl = useVariable<Variables>('graphBaseUrl');
  const driveId = useVariable<Variables>('driveId');
  const requestToken = useRequestToken();
  return useMemo(() => {
    return async function <T>(path: string, options?: RequestOptions, anonymous: Boolean = false) {
      const defaultHeaders: HeadersInit = {};

      const optionsHeaders = options?.headers as any;
      if (!optionsHeaders || !optionsHeaders['content-type']) {
        defaultHeaders['content-type'] = 'application/json';
      }

      if (!anonymous) {
        const token = await requestToken();

        defaultHeaders['Authorization'] = `Bearer ${token.accessToken}`;
      }

      const headers = options?.headers ?? [];

      if (options?.body instanceof FormData) {
        delete defaultHeaders['content-type'];
      }

      const response = await fetch(`${apiBaseUrl}/v1.0/drives/${driveId}${path}`, {
        method: 'GET',
        ...options,
        body: options?.body
          ? options?.body instanceof FormData
            ? options.body
            : JSON.stringify(options.body)
          : undefined,
        headers: {
          ...defaultHeaders,
          ...headers
        }
      });

      if (!response.ok) {
        const error = new Error(`Request failed with status ${response.status}`);
        (error as any).status = response.status;
        (error as any).response = response;
        throw error;
      }

      return response.json() as T;
    };
  }, [requestToken, apiBaseUrl]);
}

export function useAPIQuery<T>(
  key: QueryParams,
  validator?: ZodSchema<T>,
  anonymous: boolean = false,
  queryFn?: (context: QueryFunctionContext) => Promise<T>
): [T, UseQueryResult<T>] {
  const request = useAPIRequest();

  const queryKey = ['api', ...key].map(String);

  const result = useQuery<T>({
    queryKey,
    queryFn: queryFn
      ? queryFn
      : async () => {
          const anon = !!anonymous;

          const [, path, options] = queryKey as ['api', string, RequestOptions | undefined];

          try {
            return await request(path, options, anon);
          } catch (err: any) {
            if (err?.response?.status === 404) {
              return null as T;
            }
            throw err;
          }
        },
    ...context
  });

  // Because we are using suspense, we can't return undefined unless specifically set with the T | undefined type

  const data = result.data as T;

  if (validator) return [validator.parse(data), result];

  return [data, result];
}

export function useAPIMutation<T, V = unknown>(
  func: (request: ReturnType<typeof useAPIRequest>) => MutationFunction<T, V>,
  validator?: ZodSchema<T>,
  key?: QueryParams
) {
  const request = useAPIRequest();
  return useMutation<T, unknown, V>({
    mutationFn: func(request),
    ...context,
    mutationKey: key ? ['api', ...key].map(String) : undefined,
    onSuccess: async data => {
      if (validator) validator.parse(data);
    }
  });
}

export function useInvalidateQuery() {
  const client = useQueryClient();
  return async (key: QueryParams) => {
    await client.invalidateQueries({ queryKey: ['api', ...key] });
  };
}
